import { BigNumber, ethers } from 'ethers';
import { ADDRESS_ZERO } from '@uniswap/router-sdk';
import {
  parseAllowanceProperties,
  MAX_UINT160,
  MAX_UINT48,
  isTokenAddressAllowedAsElevateTargetToken,
} from './utils';

import {
  GetQuoteParams,
  GetQuoteResponse,
  ExecuteSwapParams,
  Token,
  ExecuteSwapResult,
} from './types';

import { NETWORK_ADDRESSES, NetworkAddresses } from '@/providers/swap/constants';
import { ERC20_ABI } from '../swap/abi';
import { ElevateClient } from './elevateClient';

export class ElevateSdk {
  private provider: ethers.providers.JsonRpcProvider;
  private addresses: NetworkAddresses;
  private elevateClient: ElevateClient;
  private chainId: number;

  constructor(options: { swapApiUrl?: string } = {}) {
    const mainnetChainId = 1;
    const rpcUrl: string = '/api/infura';

    this.chainId = mainnetChainId; // currently only mainnet is supported
    this.provider = new ethers.providers.JsonRpcProvider(rpcUrl);

    const addresses = NETWORK_ADDRESSES[this.chainId];
    if (!addresses) {
      throw new Error(`Network with chainId ${this.chainId} is not supported`);
    }
    this.addresses = addresses;

    this.elevateClient = new ElevateClient(options.swapApiUrl);
  }

  async getQuote({
    fromToken,
    toToken,
    amountInDecimal,
    recipient,
    signer,
  }: GetQuoteParams): Promise<GetQuoteResponse | null> {
    // Address and chainId need to be valid.
    this.throwIfInvalidTokenChainId(fromToken);
    this.throwIfInvalidTokenChainId(toToken);
    this.throwIfTokenAddressIsNotSet(fromToken);
    this.throwIfTokenAddressIsNotSet(toToken);
    this.throwIfEthTokenHasWrongValues(fromToken);
    this.throwIfEthTokenHasWrongValues(toToken);

    if (!isTokenAddressAllowedAsElevateTargetToken(toToken.address)) {
      throw new Error(
        `${toToken.symbol} token is not allowed as the target token for Elevate swaps.`
      );
    }

    const slippagePercentage = 0.3; // this is what elevate UI uses.
    const amountInWei = ethers.utils.parseUnits(amountInDecimal, fromToken.decimals);

    if (amountInWei.lte(0)) {
      throw new Error(`Amount to quote must be greater than zero`);
    }

    const qoute = await this.elevateClient.getElevateQuote({
      tokenInAddress: fromToken.address,
      tokenOutAddress: toToken.address,
      amountInWei: amountInWei.toString(), // toString formats the value using decimal base, the api expects wei in decimal base.
      recipient: recipient,
      signer: signer,
      slippagePercentage,
    });

    if (!qoute) {
      return null;
    }

    const ethValue: BigNumber =
      fromToken.address === ADDRESS_ZERO ? amountInWei : BigNumber.from(0);

    let gasEstimation: BigNumber = BigNumber.from(42_000); // Default: 42k gas. Swaps are one tx but involve multiple steps, so real usage is much higher.
    try {
      gasEstimation = await this.provider.estimateGas({
        to: this.addresses.universalRouterAddress,
        from: signer,
        data: qoute.calldata,
        value: ethValue,
      });
    } catch (error) {
      console.error('Error fetching gas estimation:', error);
    }
    const paddedGasEstimation = gasEstimation.mul(120).div(100);

    let gasPrice: BigNumber = ethers.utils.parseUnits('20', 'gwei');
    try {
      const feeData = await this.provider.getFeeData();
      gasPrice =
        feeData.maxFeePerGas || feeData.gasPrice || ethers.utils.parseUnits('20', 'gwei');
    } catch (error) {
      console.error('Error fetching gas price:', error);
    }

    const gasFee = paddedGasEstimation.mul(gasPrice);

    return {
      amountOutDecimal: ethers.utils.formatUnits(
        qoute.decoded.amountOut,
        toToken.decimals
      ),
      estimatedGasFeeWei: gasFee.toString(),
    };
  }

  async executeSwap({
    mnemonic,
    fromToken,
    toToken,
    amountInDecimal,
    slippagePercentage = 0.3,
  }: ExecuteSwapParams): Promise<ExecuteSwapResult> {
    const wallet = ethers.Wallet.fromMnemonic(mnemonic);
    const walletAddress = wallet.address;
    const connectedWallet = wallet.connect(this.provider);
    const amountInWei = ethers.utils.parseUnits(amountInDecimal, fromToken.decimals);

    await this.throwIfInvalidTokenProperties(fromToken);
    await this.throwIfInvalidTokenProperties(toToken);

    if (!isTokenAddressAllowedAsElevateTargetToken(toToken.address)) {
      throw new Error(
        `${toToken.symbol} token is not allowed as the target token for Elevate swaps.`
      );
    }

    await this.ensureEnoughBalance(walletAddress, fromToken, amountInWei);

    const needsToApproveAllowanceResult = await this.needsToApproveAllowance(
      fromToken,
      amountInWei,
      walletAddress
    );
    if (needsToApproveAllowanceResult) {
      const allowanceTxn = await this.approveAllowance(fromToken, connectedWallet);
      const allowanceTxnReceipt = await allowanceTxn.wait();
      if (allowanceTxnReceipt.status === 0) {
        throw new Error('Allowance approval transaction failed');
      }
    }

    const needsToApprovePermit2Result = await this.needsToApprovePermit2Allowance(
      fromToken,
      amountInWei,
      walletAddress
    );
    if (needsToApprovePermit2Result) {
      const permit2Txn = await this.approvePermit2Allowance(fromToken, connectedWallet);
      const permit2TxnReceipt = await permit2Txn.wait();
      if (permit2TxnReceipt.status === 0) {
        throw new Error('Permit2 allowance approval transaction failed');
      }
      await new Promise((resolve) => setTimeout(resolve, 2_500)); // wait a bit to ensure the state is updated on-chain
    }

    const swapTxn = await this.getQuoteAndPerformSwap(
      fromToken,
      toToken,
      amountInWei,
      slippagePercentage,
      connectedWallet
    );

    return { transactionHash: swapTxn.hash };
  }

  private async ensureEnoughBalance(
    walletAddress: string,
    token: Token,
    amountInWei: BigNumber
  ) {
    if (amountInWei.lte(0)) {
      throw new Error(`Amount to swap must be greater than zero`);
    }
    let balance: BigNumber = BigNumber.from(0);
    if (token.address === ADDRESS_ZERO) {
      balance = await this.provider.getBalance(walletAddress);
    } else {
      const tokenContract = new ethers.Contract(token.address, ERC20_ABI, this.provider);
      balance = await tokenContract.balanceOf(walletAddress);
    }
    if (balance.lt(amountInWei)) {
      throw new Error(`Insufficient ${token.symbol} balance`);
    }
  }

  private async needsToApproveAllowance(
    token: Token,
    amountInWei: BigNumber,
    walletAddress: string
  ): Promise<boolean> {
    if (token.address === ADDRESS_ZERO) {
      return false;
    }

    // Step 1: Check ERC20 allowance using GET /api/v1/approve
    const allowanceResult = await this.elevateClient.checkAllowance({
      owner: walletAddress,
      token: token.address,
      spender: this.addresses.permit2Address,
    });

    if (!allowanceResult) {
      throw new Error('Failed to check token allowance');
    }

    const currentAllowance = ethers.BigNumber.from(allowanceResult.allowance);

    return currentAllowance.lt(amountInWei);
  }

  private async approveAllowance(
    token: Token,
    connectedWallet: ethers.Wallet
  ): Promise<ethers.providers.TransactionResponse> {
    if (token.address === ADDRESS_ZERO) {
      throw new Error('Cannot approve allowance for ETH');
    }

    const walletAddress = connectedWallet.address;

    const approveCalldataResult = await this.elevateClient.getApproveCalldata({
      token: token.address,
      spender: this.addresses.permit2Address,
      amount: ethers.constants.MaxUint256.toString(),
    });

    if (!approveCalldataResult?.calldata) {
      throw new Error('Failed to get approve calldata');
    }

    // pad gas.
    const gasEstimation = await this.provider.estimateGas({
      to: token.address,
      from: walletAddress,
      data: approveCalldataResult.calldata,
    });
    const paddedGas = gasEstimation.mul(120).div(100);

    const txt = await connectedWallet.sendTransaction({
      to: token.address,
      data: approveCalldataResult.calldata,
      gasLimit: paddedGas,
    });

    return txt;
  }

  private async needsToApprovePermit2Allowance(
    token: Token,
    amountInWei: BigNumber,
    walletAddress: string
  ): Promise<boolean> {
    if (token.address === ADDRESS_ZERO) {
      return false;
    }

    const permit2AllowanceResult = await this.elevateClient.checkPermit2Allowance({
      owner: walletAddress,
      tokenAddress: token.address,
      approvalAddress: this.addresses.universalRouterAddress,
    });

    if (!permit2AllowanceResult) {
      throw new Error('Failed to check Permit2 allowance');
    }

    // -- Check if allowance is expired or is insufficient
    // MAX_UINT48 (281474976710655) means "no expiration" in Permit2, so it's never expired
    // Otherwise, check if expiration is 0 or in the past
    const { currentPermit2Allowance, expiration } =
      parseAllowanceProperties(permit2AllowanceResult);
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const isExpired =
      !expiration.eq(MAX_UINT48) &&
      (expiration.isZero() || expiration.lt(currentTimestamp));
    const isInsufficient =
      currentPermit2Allowance.isZero() || currentPermit2Allowance.lt(amountInWei);

    const result = isExpired || isInsufficient;
    return result;
  }

  private async approvePermit2Allowance(
    token: Token,
    connectedWallet: ethers.Wallet
  ): Promise<ethers.providers.TransactionResponse> {
    if (token.address === ADDRESS_ZERO) {
      throw new Error('Cannot approve Permit2 allowance for ETH');
    }

    const walletAddress = connectedWallet.address;

    // Permit2 uses uint48 for expiration (deadline), which should be a future Unix timestamp
    // Use a timestamp 1 year in the future (31536000 seconds = 1 year)
    // and ensure deadline does not exceed MAX_UINT48
    const currentTimestamp = Math.floor(Date.now() / 1000);
    const oneYearFromNow = currentTimestamp + 31536000;
    const MAX_UINT48Number = MAX_UINT48.toNumber();
    const deadline = Math.min(oneYearFromNow, MAX_UINT48Number).toString();

    const permit2CalldataResult = await this.elevateClient.getPermit2Calldata({
      tokenAddress: token.address,
      approvalAddress: this.addresses.universalRouterAddress,
      amount: MAX_UINT160, // Permit2 uses uint160 for amounts, not uint256
      deadline: deadline,
    });

    if (!permit2CalldataResult?.calldata) {
      throw new Error('Failed to get permit2 calldata');
    }

    const estimatedGas = await this.provider.estimateGas({
      to: this.addresses.permit2Address,
      from: walletAddress,
      data: permit2CalldataResult.calldata,
    });
    const paddedGas = estimatedGas.mul(120).div(100);

    // Send Permit2 approval transaction using the calldata from API
    const txtResult = await connectedWallet.sendTransaction({
      to: this.addresses.permit2Address,
      data: permit2CalldataResult.calldata,
      gasLimit: paddedGas,
    });
    return txtResult;
  }

  private async getQuoteAndPerformSwap(
    fromToken: Token,
    toToken: Token,
    amountInWei: BigNumber,
    slippagePercentage: number,
    connectedWallet: ethers.Wallet
  ): Promise<ethers.providers.TransactionResponse> {
    const walletAddress: string = connectedWallet.address;

    const transactionValue: BigNumber =
      fromToken.address === ADDRESS_ZERO ? amountInWei : BigNumber.from(0);

    const quote = await this.elevateClient.getElevateQuote({
      tokenInAddress: fromToken.address,
      tokenOutAddress: toToken.address,
      amountInWei: amountInWei.toString(), // toString formats the value using decimal base, the api expects wei in decimal base.
      recipient: walletAddress,
      signer: walletAddress,
      slippagePercentage,
    });

    if (!quote?.calldata) {
      throw new Error('Failed to get quote or quote missing calldata');
    }

    // pad gas.
    const gasEstimation = await this.provider.estimateGas({
      to: this.addresses.universalRouterAddress,
      from: walletAddress,
      data: quote.calldata,
      value: transactionValue,
    });
    const paddedGas = gasEstimation.mul(120).div(100);

    const txtResponse = await connectedWallet.sendTransaction({
      to: this.addresses.universalRouterAddress,
      data: quote.calldata,
      value: transactionValue,
      gasLimit: paddedGas,
    });

    return txtResponse;
  }

  private async throwIfInvalidTokenProperties(token: Token) {
    this.throwIfEthTokenHasWrongValues(token);
    this.throwIfTokenAddressIsNotSet(token);
    this.throwIfInvalidTokenChainId(token);
    await this.throwIfInvalidTokenDecimals(token);
  }

  private throwIfTokenAddressIsNotSet(token: Token) {
    if (!token.address) {
      throw new Error(`Token ${token.symbol} address is not set`);
    }
  }

  private throwIfInvalidTokenChainId(token: Token) {
    const isSameChain = token.chainId === this.chainId;
    if (!isSameChain) {
      throw new Error(
        `Token ${token.symbol} chainId ${token.chainId} does not match SDK chainId ${this.chainId}`
      );
    }
  }

  private throwIfEthTokenHasWrongValues(token: Token) {
    if (token.symbol.toUpperCase() === 'ETH' && token.address !== ADDRESS_ZERO) {
      throw new Error(
        `Token with symbol ETH must have address ADDRESS_ZERO, got: ${token.address}`
      );
    }
    if (token.address === ADDRESS_ZERO && token.symbol.toUpperCase() !== 'ETH') {
      throw new Error(
        `Token with ADDRESS_ZERO must have have ETH symbol, got: ${token.symbol}`
      );
    }
  }

  private async throwIfInvalidTokenDecimals(token: Token) {
    let decimals: number;
    if (token.address === ADDRESS_ZERO) {
      decimals = 18; // Address zero is ETH
    } else {
      const tokenContract = new ethers.Contract(token.address, ERC20_ABI, this.provider);
      decimals = await tokenContract.decimals();
    }

    if (decimals !== token.decimals) {
      throw new Error(
        `Token ${token.symbol} decimals ${token.decimals} does not match on-chain decimals ${decimals}`
      );
    }
  }
}
